import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type AdminUser,
  issuePasswordReset,
  listUsers,
  patchUser,
} from '@/features/admin/admin-api';
import { useFormatters } from '@/i18n/format';
import { ApiError } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Copy, Key, Loader2, Search, ShieldAlert, ShieldCheck, Users } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/admin/users')({
  component: AdminUsersPage,
});

function AdminUsersPage(): ReactElement {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const users = useQuery({
    queryKey: ['admin', 'users', search],
    queryFn: () => listUsers({ search: search || undefined, size: 50 }),
  });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('admin.nav.adminPanel', { defaultValue: 'Admin' })}
        title={t('admin.users.title')}
        description={t('admin.users.description')}
      />

      <div className="relative max-w-sm">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          placeholder={t('admin.users.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 pl-9"
        />
      </div>

      {users.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-24 rounded-xl" />
        </div>
      ) : (users.data?.users ?? []).length === 0 ? (
        <EmptyState icon={Users} title={t('admin.users.empty')} />
      ) : (
        <ul className="space-y-2">
          {users.data?.users.map((u) => (
            <li key={u.id}>
              <UserRow user={u} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function userInitials(email: string): string {
  const parts = email.split(/[@.\s]/u).filter(Boolean);
  const first = parts[0] ?? '';
  if (parts.length === 0) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const second = parts[1] ?? '';
  return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase();
}

function UserRow({ user }: { user: AdminUser }): ReactElement {
  const { t } = useTranslation();
  const { date } = useFormatters();
  const queryClient = useQueryClient();
  const [resetLink, setResetLink] = useState<string | null>(null);

  const disable = useMutation({
    mutationFn: () => patchUser(user.id, { disabled: !user.disabledAt }),
    onSuccess: () => {
      toast.success(
        user.disabledAt ? t('admin.users.enabledToast') : t('admin.users.disabledToast'),
      );
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('admin.users.actionFailed')),
  });

  const promote = useMutation({
    mutationFn: () =>
      patchUser(user.id, {
        platformRole: user.platformRole === 'admin' ? 'user' : 'admin',
      }),
    onSuccess: () => {
      toast.success(t('admin.users.roleUpdatedToast'));
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('admin.users.actionFailed')),
  });

  const reset = useMutation({
    mutationFn: () => issuePasswordReset(user.id),
    onSuccess: (data) => {
      setResetLink(data.reset.url);
      toast.success(t('admin.users.resetLinkCreated'));
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : t('admin.users.actionFailed')),
  });

  async function copy(): Promise<void> {
    if (!resetLink) return;
    await navigator.clipboard.writeText(resetLink);
    toast.success(t('admin.users.copySuccess'));
  }

  return (
    <Card className="border-border/70 shadow-soft">
      <CardHeader className="flex flex-row items-start gap-3 space-y-0">
        <Avatar className="h-10 w-10">
          <AvatarFallback>{userInitials(user.email)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-1">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <span className="truncate">{user.email}</span>
            {user.platformRole === 'admin' ? (
              <Badge variant="success" className="gap-1">
                <ShieldCheck className="size-3" aria-hidden />
                admin
              </Badge>
            ) : null}
            {user.disabledAt ? (
              <Badge variant="destructive" className="gap-1">
                <ShieldAlert className="size-3" aria-hidden />
                {t('admin.users.disabledSuffix')}
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            <code className="font-mono text-xs">{user.id}</code> ·{' '}
            {t('admin.users.memberships', { count: user.membershipCount })} ·{' '}
            {t('admin.users.created', { when: date(user.createdAt) })}
          </CardDescription>
          {resetLink ? (
            <div className="flex items-center gap-2 pt-2">
              <code className="flex-1 truncate rounded-md border bg-muted px-2 py-1 font-mono text-xs">
                {resetLink}
              </code>
              <Button variant="outline" size="icon" onClick={copy}>
                <Copy className="size-4" aria-hidden />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setResetLink(null)}>
                {t('admin.users.hide')}
              </Button>
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => disable.mutate()}
            disabled={disable.isPending}
          >
            {user.disabledAt ? t('admin.users.reactivate') : t('admin.users.deactivate')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => promote.mutate()}
            disabled={promote.isPending}
          >
            {user.platformRole === 'admin'
              ? t('admin.users.demoteToUser')
              : t('admin.users.promoteToAdmin')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => reset.mutate()}
            disabled={reset.isPending}
          >
            {reset.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Key className="size-3.5" aria-hidden />
            )}
            {t('admin.users.resetLink')}
          </Button>
        </div>
      </CardHeader>
    </Card>
  );
}
